import { DialogActions, DialogContent } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { isAxiosError } from 'axios'
import React from 'react'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import swapUserLocationService from 'shared/services/swapUserLocation'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import { getRefreshToken } from 'shared/utils/helpers/refreshToken'
import invalidateCarts from 'shared/utils/helpers/invalidateCarts'
import { useProfileQuery } from 'shared/store/useProfile'

interface Props {
   orderUuid: string
   notificationLocationUuid?: string
   handleClose: () => void
}

export default function ClientOrderCreatedNotification({ orderUuid, notificationLocationUuid, handleClose }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'NOTIFICATIONS'])
   const navigate = useNavigate()
   const { data: profile } = useProfileQuery()
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   function handleLocationSwap(locationUuid: string) {
      swapUserLocationService(locationUuid)
         .then((res) => {
            if (res.status === 200) {
               getRefreshToken(queryClient)
               queryClient.invalidateQueries('userProfile')
               queryClient.invalidateQueries('userStats')
               invalidateCarts(queryClient)
            }
         })
         .then(() => {
            navigate(`/dashboard/orders-history?uuid=${orderUuid}`)
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(
                  error.response?.data?.errors[0].code || t('AUTHORIZED:header.failedLocationSwap'),
                  'error'
               )
            }
         })
   }

   function handleNavigate() {
      if (!notificationLocationUuid || notificationLocationUuid === profile?.locationUuid) {
         navigate(`/dashboard/orders-history?uuid=${orderUuid}`)
      } else if (notificationLocationUuid !== profile?.locationUuid) {
         if (profile?.availableLocations.find((item) => item.uuid === notificationLocationUuid)) {
            handleLocationSwap(notificationLocationUuid)
         } else {
            addNotification(t('NOTIFICATIONS:locationPermissionError'), 'error')
         }
      }
   }

   return (
      <>
         <DialogContent>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
               {t('NOTIFICATIONS:clientOrderCreatedText')}
            </Typography>
         </DialogContent>
         <DialogActions>
            <OutlinedButton variant="outlined" onClick={handleClose}>
               {t('TRANSLATION:cancel')}
            </OutlinedButton>
            <ContainedButton variant="contained" onClick={handleNavigate}>
               {t('NOTIFICATIONS:showOrder')}
            </ContainedButton>
         </DialogActions>
      </>
   )
}
